/**
 * A card's choreography, the way the concept video builds it: one GSAP
 * timeline per card, with times in seconds from the moment the card opens.
 * The helpers are the video's own (rise, pop, letters, count, shake, press),
 * and each plays its own sound on the beat its motion lands: a soft swish for
 * a rise, a note per bounce, a marimba run for letters, a rush and a hit for
 * a slam, a rumble for the shake, ticks that slow as a count settles.
 *
 * A card's markup is its finished frame; the timeline only animates from
 * earlier states into it. So with reduced motion the timeline is never built
 * and the card simply shows that frame, silently. Pausing the story pauses
 * the timeline, sounds included.
 */

import { gsap } from "gsap";
import { useEffect, useLayoutEffect, useRef, type DependencyList } from "react";

import { prefersReducedMotion } from "../motion";
import { play, scaleStep, type SoundName, type SoundOpts } from "../sound";
import { useStoryPaused } from "./stage";

type Targets = gsap.TweenTarget;
type Opts = { y?: number; x?: number; dur?: number; stagger?: number; ease?: string; from?: number; sound?: SoundName | false };

export type Story = {
  tl: gsap.core.Timeline;
  /** Elements inside the card matching a selector. */
  q: (selector: string) => HTMLElement[];
  /** Fade up into place. */
  rise: (targets: Targets, at: number, o?: Opts) => void;
  /** Scale up from small with an overshoot. */
  pop: (targets: Targets, at: number, o?: Opts) => void;
  /** Letters (`.ch`) inside the targets tumble up into place. */
  letters: (targets: Targets, at: number, o?: Opts & { rot?: number }) => void;
  /** Words (`.wd`) inside the targets hop up into place. */
  words: (targets: Targets, at: number, o?: Opts) => void;
  /** Drop in from huge and land with a thump. */
  slam: (target: Targets, at: number, o?: Opts) => void;
  /** Rattle a layer, as if something heavy landed. */
  shake: (target: Targets, at: number, times?: number, amp?: number) => void;
  /** Roll a number up from `from` (default 0), ticking, with a ding at the end. */
  count: (el: Element | null | undefined, to: number, at: number, dur: number, format?: (v: number) => string, o?: { from?: number; ease?: string }) => void;
  /** A quick squeeze, like a finger pressing it. */
  press: (el: Targets, at: number) => void;
  sound: (name: SoundName, at: number, pitch?: number, opts?: SoundOpts) => void;
  /** `n` sounds along an eased motion, one each time it passes another 1/(n+1) of the way. */
  ticksAlong: (at: number, dur: number, ease: string, n: number, name?: SoundName) => void;
  /** A steady pseudo-random number in [0, 1), the same on every replay. */
  random: () => number;
};

const plain = (v: number) => Math.round(v).toLocaleString("en-US");

/** Inner elements of the targets matching a selector (the targets themselves if they match). */
function inside(targets: Targets, selector: string): Element[] {
  return gsap.utils.toArray<Element>(targets).flatMap((el) => (el.matches(selector) ? [el] : [...el.querySelectorAll(selector)]));
}

/** Up to `cap` of `n` items, spread evenly, so a long run still sounds like a run, not a buzz. */
function spread(n: number, cap: number): number[] {
  if (n <= cap) return Array.from({ length: n }, (_, i) => i);
  return Array.from({ length: cap }, (_, k) => Math.round((k * (n - 1)) / (cap - 1)));
}

/** When an eased motion passes each of `n` evenly spaced points: ticks that slow as the motion settles. */
export function easedBeats(ease: string, n: number): number[] {
  const curve = gsap.parseEase(ease) as (t: number) => number;
  return Array.from({ length: n }, (_, k) => {
    const goal = (k + 1) / (n + 1);
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (curve(mid) < goal) lo = mid;
      else hi = mid;
    }
    return (lo + hi) / 2;
  });
}

function story(tl: gsap.core.Timeline, root: HTMLElement): Story {
  let seed = 26;
  const random = () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
  const sound = (name: SoundName, at: number, pitch = 1, opts?: SoundOpts) => {
    tl.call(() => play(name, pitch, opts), undefined, Math.max(0, at));
  };
  // Each card plays in its own key, and later things land a little higher, so a card's
  // arrivals climb as it fills and no two cards sound the same.
  const key = scaleStep(Math.floor(Math.random() * 5));
  const pitchAt = (at: number) => key * (1 + Math.min(at, 4) * 0.06);
  const count = (targets: Targets) => gsap.utils.toArray(targets).length;
  /** One sound per item, each on its own beat and a step up the scale. */
  const run = (name: SoundName, n: number, at: number, stagger: number, land: number, cap: number) => {
    for (const i of spread(n, cap)) sound(name, at + i * stagger + land, pitchAt(at) * scaleStep(i));
  };

  return {
    tl,
    random,
    sound,
    q: (selector) => [...root.querySelectorAll<HTMLElement>(selector)],
    rise(targets, at, o) {
      tl.fromTo(
        targets,
        { y: o?.y ?? 60, x: o?.x ?? 0, autoAlpha: 0 },
        { y: 0, x: 0, autoAlpha: 1, duration: o?.dur ?? 0.6, stagger: o?.stagger ?? 0.08, ease: o?.ease ?? "power3.out" },
        at,
      );
      // A glide up is soft: one quiet swish, however many things rise together.
      if (o?.sound !== false) sound(o?.sound ?? "swish", at, pitchAt(at));
    },
    pop(targets, at, o) {
      const stagger = o?.stagger ?? 0.06;
      tl.fromTo(
        targets,
        { scale: o?.from ?? 0.3, autoAlpha: 0 },
        { scale: 1, autoAlpha: 1, duration: o?.dur ?? 0.6, stagger, ease: o?.ease ?? "back.out(2)" },
        at,
      );
      if (o?.sound === false) return;
      if (o?.sound) sound(o.sound, at, pitchAt(at));
      // Each one bounces in on its own beat, a note higher than the last.
      else run("pop", count(targets), at, stagger, 0.05, 8);
    },
    letters(targets, at, o) {
      const chars = inside(targets, ".ch");
      const stagger = o?.stagger ?? 0.035;
      const dur = o?.dur ?? 0.55;
      tl.fromTo(
        chars,
        { yPercent: o?.y ?? 90, autoAlpha: 0, rotation: o?.rot ?? 6 },
        { yPercent: 0, autoAlpha: 1, rotation: 0, duration: dur, stagger, ease: o?.ease ?? "back.out(1.6)" },
        at,
      );
      if (o?.sound === false) return;
      if (o?.sound) sound(o.sound, at, pitchAt(at));
      // Letters tumble in one after another: a marimba run, each note as its letter lands.
      else run("pluck", chars.length, at, stagger, dur * 0.3, 14);
    },
    words(targets, at, o) {
      const words = inside(targets, ".wd");
      const stagger = o?.stagger ?? 0.06;
      const dur = o?.dur ?? 0.45;
      tl.fromTo(
        words,
        { yPercent: o?.y ?? 80, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1, duration: dur, stagger, ease: o?.ease ?? "back.out(1.6)" },
        at,
      );
      if (o?.sound === false) return;
      if (o?.sound) sound(o.sound, at, pitchAt(at));
      else run("knock", words.length, at, stagger, dur * 0.3, 10);
    },
    slam(target, at, o) {
      const dur = o?.dur ?? 0.3;
      tl.fromTo(target, { scale: o?.from ?? 3.4, autoAlpha: 0 }, { scale: 1, autoAlpha: 1, duration: dur, ease: o?.ease ?? "power4.in" }, at);
      if (o?.sound === false) return;
      // The rush grows as the type falls and stops dead on the hit.
      sound("riser", at, 1, { length: dur });
      sound(o?.sound ?? "slam", at + dur, pitchAt(at) * 0.95);
    },
    shake(target, at, times = 9, amp = 22) {
      const wobble = gsap.timeline();
      for (let i = 0; i < times; i++) {
        const fade = 1 - i / times;
        wobble.to(target, { x: (random() * 2 - 1) * amp * fade, y: (random() * 2 - 1) * amp * fade, duration: 0.03, ease: "none" });
      }
      wobble.to(target, { x: 0, y: 0, duration: 0.05, ease: "none" });
      tl.add(wobble, at);
      sound("rumble", at, 1, { length: times * 0.03 + 0.05 });
    },
    count(el, to, at, dur, format = plain, o) {
      if (!el) return;
      const value = { v: o?.from ?? 0 };
      el.textContent = format(value.v);
      // A tick each time the number moves another 1/24 of the way: they slow as it settles.
      const steps = 24;
      let lastStep = 0;
      tl.to(
        value,
        {
          v: to,
          duration: dur,
          ease: o?.ease ?? "power2.out",
          onUpdate() {
            el.textContent = format(value.v);
            const step = Math.floor(this.ratio * steps);
            if (step > lastStep && step < steps) {
              lastStep = step;
              play("tick", 1 + this.ratio * 0.6);
            }
          },
        },
        at,
      );
      sound("ding", at + dur);
    },
    ticksAlong(at, dur, ease, n, name = "tick") {
      easedBeats(ease, n).forEach((t, k) => sound(name, at + t * dur, 1 + (k / n) * 0.6));
    },
    press(el, at) {
      tl.to(el, { scale: 0.94, duration: 0.1, ease: "power1.in" }, at);
      tl.to(el, { scale: 1, duration: 0.3, ease: "back.out(3)" }, at + 0.1);
    },
  };
}

/**
 * Build a card's timeline once it is on screen. Returns the ref for the
 * card's root element; selectors in `build` look inside it.
 */
export function useTimeline<T extends HTMLElement = HTMLDivElement>(build: (s: Story) => void, deps: DependencyList = []) {
  const root = useRef<T>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);
  const paused = useStoryPaused();
  const pausedNow = useRef(paused);
  pausedNow.current = paused;

  useLayoutEffect(() => {
    const el = root.current;
    if (!el || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ paused: true, defaults: { ease: "power3.out" } });
      build(story(tl, el));
      timeline.current = tl;
      if (!pausedNow.current) tl.play();
    }, el);
    return () => {
      timeline.current = null;
      ctx.revert();
    };
    // The card's data never changes while it is up; callers pass deps if theirs can.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const tl = timeline.current;
    if (!tl) return;
    if (paused) tl.pause();
    else if (tl.progress() < 1) tl.resume();
  }, [paused]);

  return root;
}
